/**
 * URL Utility Functions
 * Helpers for normalizing and handling URLs
 */

/**
 * Normalize website URLs by ensuring they have a proper protocol (https://)
 *
 * @param url - The URL to normalize
 * @returns The normalized URL with https:// protocol, or null if URL is empty
 *
 * @example
 * normalizeWebsiteUrl('www.example.com') // Returns: 'https://www.example.com'
 * normalizeWebsiteUrl('http://example.com') // Returns: 'http://example.com'
 * normalizeWebsiteUrl('https://example.com') // Returns: 'https://example.com'
 * normalizeWebsiteUrl('example.com') // Returns: 'https://example.com'
 */
export const normalizeWebsiteUrl = (url: string | null | undefined): string | null => {
  if (!url) return null

  const trimmedUrl = url.trim()
  if (!trimmedUrl) return null

  // If already has protocol (http:// or https://), return as is
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl
  }

  // Add https:// protocol to URLs without protocol
  // This handles cases like:
  // - www.example.com
  // - example.com
  // - subdomain.example.com
  return `https://${trimmedUrl}`
}

/**
 * Open a URL in a new browser tab
 * Automatically normalizes the URL before opening
 *
 * @param url - The URL to open
 * @returns true if URL was opened successfully, false otherwise
 */
export const openUrl = (url: string | null | undefined): boolean => {
  const normalizedUrl = normalizeWebsiteUrl(url)

  if (normalizedUrl) {
    window.open(normalizedUrl, '_blank', 'noopener,noreferrer')
    return true
  }

  return false
}

/**
 * Validate if a string is a valid URL format
 *
 * @param url - The URL to validate
 * @returns true if URL is valid, false otherwise
 */
export const isValidUrl = (url: string | null | undefined): boolean => {
  if (!url) return false

  try {
    const normalizedUrl = normalizeWebsiteUrl(url)
    if (!normalizedUrl) return false

    new URL(normalizedUrl)
    return true
  } catch {
    return false
  }
}
